import NodeStatus from 'components/NodeStatus/NodeStatus';
import NodeStatusIcon from 'components/NodeStatus/NodeStatusIcon';
import BaseTable from 'components/Tables/BaseTable';
import { usePerms } from 'contexts/PermsContext';
import { DeviceRow } from 'features/integrations/features/ConnectedDeviceGroups/components/DeviceRows';
import { hasDevicePermissionFromDevice } from 'features/integrations/features/ConnectedSystems/utils/devicePermissionUtils';
import { selectVisibleDeviceTypes } from 'features/integrations/state/selectors/integrationSelectors';
import {
  openAwareSettings,
  openDeviceSettings,
  setLinkingGroupId,
} from 'features/integrations/state/slices/deviceSlice';
import { setSelectedDevice } from 'features/integrations/state/thunks';
import { DeviceIsSystemType } from 'features/integrations/utils/deviceComparisionUtils';
import { groupSearchFilter } from 'features/integrations/utils/integrationSearchUtils';
import { useMemo, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import { FaCog, FaExpandAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { useGetGroupsQuery } from 'services/devices/groups/groups.api';
import { deviceImageList, getDeviceListForGroup } from 'utils/iconUtils';
import { GroupDeviceSettingsModal } from '../../../Modals';

export default function DeviceGroupList({ onOpenDevice, widget = false }) {
  const dispatch = useDispatch();
  const perms = usePerms();
  const isWidget = widget === true;
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const {
    data: deviceGroups = [],
    isLoading: deviceGroupsLoading,
    isError: deviceGroupsError,
  } = useGetGroupsQuery();
  // Tracks which group is open in the settings modal.
  const [settingsGroupId, setSettingsGroupId] = useState(null);
  const visibleDeviceTypes = useSelector(selectVisibleDeviceTypes);
  const [params] = useSearchParams();

  // Pick a representative device for group status (alerting/maintenance/offline).
  const getGroupStatus = (group) => {
    // Merge device IDs from all group buckets (devices/controllers/aux).
    const allDeviceIds = [
      ...(group.devices || []),
      ...(group.controllers || []),
      ...(group.auxDevices || []),
    ];
    const uniqueDeviceIds = Array.from(new Set(allDeviceIds));

    const devices = connectedDevices.filter((device) => uniqueDeviceIds.includes(device.id));
    // Empty group: nothing to evaluate.
    if (devices.length === 0) {
      return null;
    }

    // Only consider devices with status data populated.
    const statusDevices = devices.filter(
      (device) =>
        device &&
        device.data &&
        Array.isArray(device.data) &&
        device.data.length > 0 &&
        device.data[0] &&
        typeof device.data[0].status !== 'undefined',
    );

    // Fallback when status payload is missing.
    if (statusDevices.length === 0) {
      // console.log(
      //   `No status devices found for group ${group.name}, using first device as fallback`
      // );
      return devices.length > 0 ? devices[0] : null;
    }

    // Prefer maintenance, then alerting, then offline, then normal.
    const hasAlertingDevice = statusDevices.some((device) => device.data[0].status);
    const hasOfflineDevice = statusDevices.some((device) => device.data[0].is_offline);
    const hasMaintenanceDevice = statusDevices.some(
      (device) => device.kill_switch || device.data[0].status === -1,
    );

    let useThisDevice = statusDevices.find((device) => {
      if (hasMaintenanceDevice) {
        return device.kill_switch || device.data[0].status === -1;
      }
      if (hasAlertingDevice) {
        return device.data[0].status === 1;
      }
      if (hasOfflineDevice) {
        return device.is_offline;
      }
      return device.data[0].status === 1;
    });

    if (!useThisDevice) {
      useThisDevice = statusDevices.find((device) => device.data[0].status === 0);
    }

    // Final fallback - return first status device
    if (!useThisDevice) {
      useThisDevice = statusDevices[0];
    }

    return useThisDevice;
  };

  // Determine group type based on the group's type field (not individual devices)
  const getGroupType = (group) => {
    // Use the group's type field directly
    if (
      group.type === 'Technology Bridge_Flashing Lights' ||
      group.type === 'Manual Road Closure'
    ) {
      return 'Road Closures';
    } else if (group.type === 'Technology Bridge_Siren/Giant Voice') {
      return 'Sirens';
    }
    // If group type doesn't match our filter categories, return null (show anyway)
    return null;
  };

  // Check if group should be visible based on filters
  const isGroupVisible = (group) => {
    const effectiveVisibleDeviceTypes = visibleDeviceTypes || visibleDeviceTypes;
    if (!effectiveVisibleDeviceTypes) return true;

    // Get the group type (Road Closures, Sirens, etc.)
    const groupType = getGroupType(group);

    // If group has no recognized type, show it (mixed groups, empty groups, etc.)
    if (!groupType) return true;

    // Check if the group type is enabled in the filter
    return effectiveVisibleDeviceTypes[groupType] !== false;
  };

  // Filter groups based on visibility
  const filteredGroups = deviceGroups?.filter(isGroupVisible)?.filter(
    widget
      ? (group) => {
          const search = params.get('search');
          if (!search) return true;
          const searchTerm = search.replace(/\s+/g, '').toLowerCase();
          return (
            group.name?.toLowerCase().includes(searchTerm) ||
            String(group.id).toLowerCase().includes(searchTerm)
          );
        }
      : (group) => groupSearchFilter(group, connectedDevices),
  );

  const settingsGroup = useMemo(
    () => deviceGroups?.find((group) => group.id === settingsGroupId) || null,
    [deviceGroups, settingsGroupId],
  );

  const onOpenGroupSettingsModal = (groupId) => {
    if (isWidget) return;
    setSettingsGroupId(groupId);
  };

  const onCloseGroupSettingsModal = () => setSettingsGroupId(null);

  const renderGroupDevices = (group, deviceIds, isEmpty, compact = false) => {
    if (isEmpty) return <div>No devices in this group</div>;
    if (compact) {
      return (
        <div className="d-flex flex-column gap-2 bg-primary p-2 rounded-2">
          {deviceIds.map((deviceId, index) => {
            const device = connectedDevices.find((item) => item.id === deviceId);
            if (!device) return null;
            const canEdit = hasDevicePermissionFromDevice(perms, device, 'createEdit');
            return (
              <div
                key={`${group.id}-${deviceId}-${index}`}
                className={`d-flex align-items-center justify-content-between gap-2 py-2 ${
                  index === deviceIds.length - 1 ? '' : 'border-bottom border-secondary'
                }`}
                onClick={() => {
                  if (isWidget) {
                    dispatch(setSelectedDevice({ id: device.id }));
                  } else {
                    dispatch(setSelectedDevice({ index: -1, id: device.id }));
                  }
                  onOpenDevice(device.id);
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <img
                    src={deviceImageList[device?.device_type] || deviceImageList['RESCALERT']}
                    alt={device?.device_type || 'Rescalert'}
                    width={'22px'}
                  />
                  <div className="d-flex flex-column">
                    <span className="text-break">{device.alias}</span>
                    <span className="text-secondary small">ID: {device.node_id}</span>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <NodeStatusIcon device={device} />
                  {canEdit && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-white"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (DeviceIsSystemType(device, 'Flood')) {
                          dispatch(openAwareSettings(device.id));
                        } else {
                          dispatch(openDeviceSettings(device.id));
                        }
                      }}
                    >
                      <FaCog size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div className="w-100 px-3 py-2">
        <div className="row px-2 py-1 border-bottom border-secondary fw-bold align-items-center">
          <div className="col-12 col-md-4 col-lg-5">Device</div>
          <div className="col-12 col-md-3 col-lg-2">Available Licenses</div>
          <div className="col-12 col-md-3 col-lg-4">Status</div>
          <div className="col-12 col-md-2 col-lg-1" />
        </div>
        {deviceIds.map((deviceId, index) => (
          <DeviceRow
            key={`${group.id}-${deviceId}-${index}`}
            deviceId={deviceId}
            last={index === deviceIds.length - 1}
            hideDate
          />
        ))}
      </div>
    );
  };

  const dropdownMenu = (group) => {
    const deviceIds = Array.isArray(group?.devices) ? group.devices : [];
    return renderGroupDevices(group, deviceIds, deviceIds.length === 0);
  };

  const groupRows =
    filteredGroups?.map((group) => ({
      ...group,
      devicesCount: group.devices?.length ?? 0,
      groupStatus: getGroupStatus(group),
    })) || [];

  const groupColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      Cell: ({ row }) => {
        const group = row.original;
        return (
          <div className="d-flex align-items-center gap-2">
            <img
              className="d-none d-md-block"
              src={getDeviceListForGroup(group)}
              alt={group?.type || 'RescAlert'}
              width={'24px'}
            />
            <span
              role={isWidget ? 'text' : 'button'}
              tabIndex={isWidget ? -1 : 0}
              className={isWidget ? 'text-break' : 'text-break text-decoration-underline'}
              onClick={(event) => {
                if (isWidget) return;
                event.stopPropagation();
                onOpenGroupSettingsModal(group.id);
              }}
              onKeyDown={(event) => {
                if (isWidget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.stopPropagation();
                  onOpenGroupSettingsModal(group.id);
                }
              }}
            >
              {group.name}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'devicesCount',
      header: 'Devices',
      size: 90,
      minSize: 70,
      maxSize: 140,
      grow: false,
      Cell: ({ cell }) => <span>{cell.getValue()}</span>,
    },
    {
      accessorKey: 'groupStatus',
      header: 'Status',
      size: 140,
      minSize: 120,
      maxSize: 200,
      grow: false,
      Cell: ({ cell }) => <NodeStatus device={cell.getValue()} />,
    },
    {
      id: 'settings',
      header: '',
      size: 60,
      minSize: 48,
      maxSize: 80,
      grow: false,
      Cell: ({ row }) => {
        if (isWidget) return null;
        return (
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-link p-0 text-white"
              onClick={(event) => {
                event.stopPropagation();
                onOpenGroupSettingsModal(row.original.id);
              }}
            >
              <FaCog size={18} />
            </button>
          </div>
        );
      },
    },
    ...(isWidget
      ? [
          {
            id: 'expand',
            header: '',
            size: 44,
            minSize: 36,
            maxSize: 60,
            grow: false,
            Cell: ({ row }) => (
              <div className="d-flex justify-content-end">
                <FaExpandAlt
                  size={18}
                  style={{ cursor: 'pointer' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    window.open(`/system/integrations?guid=${row.original.id}`);
                  }}
                />
              </div>
            ),
          },
        ]
      : []),
  ];

  // Link a selected group to the Map + Graph. Disabled in widget mode.
  const setLinkGroup = (group) => {
    if (isWidget) return;
    dispatch(setLinkingGroupId(group.id));
  };

  return (
    <>
      <div className="d-none d-xxl-block">
        <BaseTable
          columns={groupColumns}
          rows={groupRows}
          expandable
          onRowClick={setLinkGroup}
          enableRowVirtualization={groupRows.length > 50}
          dropdownMenu={dropdownMenu}
          altRowSettings={{
            alternate: true,
            color1: '#222A2D',
            color2: '#2C373B',
          }}
          customStyles={{
            cell: { color: '#ffffff', borderBottom: '1px solid #56577A' },
            header: { backgroundColor: '#2C373B' },
          }}
        />
      </div>

      <Accordion
        alwaysOpen
        defaultActiveKey={filteredGroups?.map((group) => String(group.id)) || []}
        className="d-block d-xxl-none bordered-table-accordion"
      >
        {filteredGroups?.map((group, index) => {
          const deviceIds = Array.isArray(group?.devices) ? group.devices : [];
          const isEmpty = deviceIds.length === 0;
          const groupStatus = getGroupStatus(group);

          return (
            <Accordion.Item
              className="bordered-table-accordion-item"
              id={group.id}
              eventKey={String(group.id)}
              key={group.id}
            >
              <Accordion.Header>
                <div
                  className="d-flex w-100 justify-content-between align-items-center gap-2 pe-2"
                  onClick={() => setLinkGroup(group)}
                >
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={getDeviceListForGroup(group)}
                      alt={group?.type || 'RescAlert'}
                      width={'24px'}
                      height={'24px'}
                    />
                    <div className="d-flex flex-column">
                      <span
                        role={isWidget ? 'text' : 'button'}
                        tabIndex={isWidget ? -1 : 0}
                        className={isWidget ? 'text-break' : 'text-break text-decoration-underline'}
                        onClick={(event) => {
                          if (isWidget) return;
                          event.stopPropagation();
                          onOpenGroupSettingsModal(group.id);
                        }}
                        onKeyDown={(event) => {
                          if (isWidget) return;
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                            onOpenGroupSettingsModal(group.id);
                          }
                        }}
                      >
                        {group.name}
                      </span>
                      <span className="text-secondary">ID: {group.id}</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    {!isEmpty && <NodeStatusIcon device={groupStatus} />}
                    {!isWidget && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="btn btn-link p-0 text-white"
                        onClick={(event) => {
                          event.stopPropagation();
                          onOpenGroupSettingsModal(group.id);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                            onOpenGroupSettingsModal(group.id);
                          }
                        }}
                      >
                        <FaCog size={18} />
                      </span>
                    )}
                    {isWidget && (
                      <span
                        role="button"
                        tabIndex={0}
                        className="text-white"
                        onClick={(event) => {
                          event.stopPropagation();
                          window.open(`/system/integrations?guid=${group.id}`);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.stopPropagation();
                            window.open(`/system/integrations?guid=${group.id}`);
                          }
                        }}
                      >
                        <FaExpandAlt size={18} />
                      </span>
                    )}
                  </div>
                </div>
              </Accordion.Header>
              <Accordion.Body className="bg-primary">
                {renderGroupDevices(group, deviceIds, isEmpty, true)}
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
      <GroupDeviceSettingsModal
        show={!!settingsGroup}
        onClose={onCloseGroupSettingsModal}
        group={settingsGroup}
      />
    </>
  );
}
